import { Dispatch, SetStateAction, useState, useEffect } from "react";
import { NavItem } from "../../data/types";
import { cn } from "../../lib/utils";
import { Icons } from "../icons";
import { Link, useLocation } from "react-router-dom";

interface DashboardNavProps {
  items: NavItem[];
  setOpen?: Dispatch<SetStateAction<boolean>>;
}

export function DashboardNav({ items, setOpen }: DashboardNavProps) {
  const { pathname } = useLocation();
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);

  // Check if the current pathname matches a sub-menu or a parent link
  const isSubMenuActive = (href: string) => {
    if (href === "/admin-dashboard") return pathname === href;
    return pathname.startsWith(href);
  };

  useEffect(() => {
    // Check if we are already inside any sub-menu of the "Mediatheque" parent
    const activeParent = items.find((item) =>
      item.subMenu?.some((subItem) => pathname.startsWith(subItem.href || ""))
    );

    // Set the parent open if we are inside its sub-menu
    if (activeParent) {
      setOpenSubMenu(activeParent.title);
    } else {
      // Reset the open sub-menu when we're not inside any sub-menu
      setOpenSubMenu(null);
    }
  }, [pathname, items]);

  const handleSubMenuToggle = (title: string) => {
    setOpenSubMenu(openSubMenu === title ? null : title); // Toggle the sub-menu open/close
  };

  if (!items?.length) {
    return null;
  }

  return (
    <nav className="grid items-start gap-2">
      {items.map((item, index) => {
        const Icon = Icons[item.icon || "arrowRight"];
        const isActive = isSubMenuActive(item.href || "/");

        return (
          <div key={index}>
            {item.subMenu ? (
              <div>
                {/* Parent Link (Mediatheque in this case) */}
                <Link
                  to={item.href || "#"}
                  onClick={(e) => {
                    // Prevent the default anchor behavior for `href: '#'`
                    e.preventDefault();
                    setOpen && setOpen(false);
                    handleSubMenuToggle(item.title); // Toggle sub-menu open/close
                  }}
                >
                  <span
                    className={cn(
                      "group flex items-center rounded-md px-3 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground",
                      isActive ? "bg-accent" : "transparent"
                    )}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    <span>{item.title}</span>
                  </span>
                </Link>

                {/* Sub-menu for the parent link */}
                {openSubMenu === item.title && (
                  <div className="mt-2 ml-6 space-y-2">
                    {item.subMenu.map((subItem, subIndex) => {
                      const SubIcon =
                        Icons[subItem?.icon as keyof typeof Icons] ||
                        Icons["user"];
                      const isSubActive = isSubMenuActive(subItem.href || "/");

                      return (
                        <Link
                          key={subIndex}
                          to={subItem.href || "/"}
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent sub-menu from closing when clicking a sub-item
                          }}
                        >
                          <span
                            className={cn(
                              "flex items-center rounded-md px-3 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground",
                              isSubActive ? "bg-accent" : "transparent"
                            )}
                          >
                            <SubIcon className="w-4 h-4 mr-2" />
                            <span>{subItem.title}</span>
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              item.href && (
                <Link
                  key={index}
                  to={item.disabled ? "/" : item.href}
                  onClick={() => {
                    if (setOpen) setOpen(false);
                  }}
                >
                  <span
                    className={cn(
                      "group flex items-center rounded-md px-3 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground",
                      isActive ? "bg-accent" : "transparent",
                      item.disabled && "cursor-not-allowed opacity-80"
                    )}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    <span>{item.title}</span>
                  </span>
                </Link>
              )
            )}
          </div>
        );
      })}
    </nav>
  );
}
